import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import {
  AlarmClock,
  Archive,
  Box,
  BoxesIcon,
  Group,
  MapPinned,
  PackageOpen,
  ScanBarcode,
} from 'lucide-react';
import { useState } from 'react';

import type { FilterDefs, FilterState, View } from '@/components/data-table';
import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  DataTableViewMenu,
  useDataTable,
  useDataTableState,
  useDataTableViews,
} from '@/components/data-table';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { LIVE_DATA_INTERVAL_MS } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { QueryInventoriesProps, SortField } from '@/gateways/impl/InventoryGateway';
import { SkuBadge } from '@/pages/seller/components/sku-badge';
import { formatNumber } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { locationInventoryStatusType } from '@/shared/status-maps';
import { locationInventoryStatusTitle } from '@/shared/title-maps';

import { InventoryListItem, InventoryListMapper } from './inventory-list-mapper';

const columnHelper = createColumnHelper<InventoryListItem>();
const columns = [
  columnHelper.accessor('skuName', {
    header: 'SKU / Description',
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <SkuBadge name={getValue() ?? 'Unknown SKU'} />
        {row.original.skuDescription ? (
          <span className="min-w-0 truncate text-sm">{row.original.skuDescription}</span>
        ) : null}
      </div>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('locationName', { header: 'Location' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (
      <StatusBadge
        // TODO ENG-3956: remove toLowerCase() once we decide on the status type ENUM
        status={`${getValue()}`.toLowerCase()}
        type={locationInventoryStatusType}
        title={locationInventoryStatusTitle}
      />
    ),
    enableSorting: false,
  }),
  columnHelper.accessor('qtyExpected', {
    header: 'Expected',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyOnHand', {
    header: 'On Hand',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyReserved', {
    header: 'Reserved',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('qtyAvailable', {
    header: 'Available',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
];

const presetViews: View[] = [{ id: 'all', name: 'All' }];

// TODO: remove once all old filter states have been converted
const convertOldFilters = (filters: Record<string, any>) => {
  const filterState: FilterState<FilterDefs> = [];
  if (filters.skuIds) {
    filterState.push({
      id: 'skuName',
      operator: 'in',
      value: (Array.isArray(filters.skuIds) ? filters.skuIds : [filters.skuIds]).map(
        ({ title }) => title,
      ),
    });
  }
  if (filters.locationIds) {
    filterState.push({
      id: 'location',
      operator: 'in',
      value: (Array.isArray(filters.locationIds) ? filters.locationIds : [filters.locationIds]).map(
        ({ title }) => title,
      ),
    });
  }
  if (filters.archived) {
    filterState.push({
      id: 'isSkuArchived',
      operator: 'eq',
      value: filters.archived === 'true',
    });
  }
  return filterState;
};

const tieBreakerSort = {
  key: 'id',
  direction: 'asc',
} as const;

export function InventoryQuantities() {
  const { inventoryGateway, productsGateway, accountsGateway } = useGateways();
  const [isLive, setIsLive] = useState(false);

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }) => accountsGateway.getLocations(undefined, { signal }),
    select: ({ items }) => items.map((l) => l.name),
  });

  const { views, isLoadingSavedViews, ...viewTabsProps } = useDataTableViews({
    tableId: 'inventoryQuantities',
    presetViews,
    convertOldFilters,
  });

  const { state, currentView, onChangeViewId, filters, ...tableProps } = useDataTableState({
    columns,
    filterDefs: {
      skuName: {
        variant: 'multiselect',
        label: 'SKU',
        icon: ScanBarcode,
        options: async (input, options) => {
          const result = await productsGateway.getSkus(
            { search: input.search, take: input.take },
            options,
          );
          return {
            items: result.items.map((sku) => sku.name),
          };
        },
      },
      location: {
        variant: 'multiselect',
        label: 'Location',
        icon: MapPinned,
        options: locations ?? [],
      },
      qtyExpected: {
        variant: 'number',
        label: 'Expected',
        icon: AlarmClock,
      },
      qtyOnHand: {
        variant: 'number',
        label: 'On Hand',
        icon: Box,
      },
      qtyReserved: {
        variant: 'number',
        label: 'Reserved',
        icon: Group,
      },
      qtyAvailable: {
        variant: 'number',
        label: 'Available',
        icon: PackageOpen,
      },
      isSkuArchived: {
        variant: 'boolean',
        label: 'SKU Status',
        icon: Archive,
        labels: {
          truthy: 'Archived',
          falsey: 'Active',
        },
      },
    },
    views,
    isLoadingSavedViews,
  });

  // TODO when in-lined, TS cannot infer QueryInventoriesProps type and allows anything
  const filterMap = filters.getActiveFilterMap();
  const queryInventoriesProps: QueryInventoriesProps = {
    take: state.pagination.pageSize,
    offset: state.pagination.pageSize * state.pagination.pageIndex,
    search: filters.search,
    sortBy: [
      ...state.sorting.map((sort) => ({
        key: sort.id as SortField,
        direction: sort.desc ? ('desc' as const) : ('asc' as const),
      })),
      tieBreakerSort,
    ],
    filters: {
      skuName: filterMap.skuName,
      locationName: filterMap.location,
      qtyExpected: filterMap.qtyExpected,
      qtyOnHand: filterMap.qtyOnHand,
      qtyReserved: filterMap.qtyReserved,
      qtyAvailable: filterMap.qtyAvailable,
      isSkuArchived: filterMap.isSkuArchived,
    },
  };

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'inventory-quantities',
    input: queryInventoriesProps,
    query: (input, { signal }) => inventoryGateway.queryInventories(input, { signal }),
    select: InventoryListMapper.toData,
    refetchInterval: isLive ? LIVE_DATA_INTERVAL_MS : undefined,
    placeholderData: keepPreviousData,
  });

  const table = useDataTable({
    data: data?.data,
    rowCount: data?.total,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    state,
    ...tableProps,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <BoxesIcon />
        </HeaderIcon>
        <HeaderTitle>Inventory Quantities</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableViewMenu
            views={views}
            currentView={currentView}
            filterState={filters.filterState}
            rowCount={data?.total}
            isLoadingRowCount={isLoading || isPlaceholderData}
            onChangeViewId={onChangeViewId}
            {...viewTabsProps}
          />
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} isLive={isLive} onChangeLive={setIsLive} />
        </DataTableToolbar>
        <DataTable table={table} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
